import { cliente } from '@/lib/cliente';
import type {
  EstadoSolicitudPedidoBC,
  ListaSolicitudesPedidoBCResponse,
  RespuestaApi,
  SolicitudPedidoBC,
} from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Solicitudes de envío a BC pendientes de autorización (o el estado que se
 * pida). Pantalla `/admin/pedidos-bc` — rol Ingenieria/Admin.
 */
export function useSolicitudesBc(estado?: EstadoSolicitudPedidoBC) {
  return useQuery<RespuestaApi<ListaSolicitudesPedidoBCResponse>>({
    queryKey: ['pedidos-bc-solicitudes', estado ?? 'pendiente'],
    queryFn: () => cliente.pedidosBc.listar(estado),
  });
}

/**
 * Aprueba una solicitud: dispara el envío real a BC en ese momento. Invalida
 * el listado, la colada asociada (si está cacheada) y el listado general de
 * coladas (el N° de pedido BC recién quedó asignado).
 */
export function useAprobarSolicitudBc() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<SolicitudPedidoBC>, Error, { id: number }>({
    mutationFn: ({ id }) => cliente.pedidosBc.aprobar(id),
    onSuccess: (respuesta) => {
      if (respuesta.data) {
        toast.success(
          respuesta.data.numero_pedido_bc
            ? `Solicitud aprobada — Pedido BC ${respuesta.data.numero_pedido_bc} creado`
            : 'Solicitud aprobada',
        );
        qc.invalidateQueries({ queryKey: ['pedidos-bc-solicitudes'] });
        qc.invalidateQueries({ queryKey: ['colada', respuesta.data.id_colada] });
        qc.invalidateQueries({ queryKey: ['coladas'] });
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo aprobar la solicitud', { description: e.message });
    },
  });
}

/**
 * Rechaza una solicitud con motivo. La colada queda libre para que el
 * operador corrija la obra y solicite de nuevo.
 */
export function useRechazarSolicitudBc() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<SolicitudPedidoBC>, Error, { id: number; motivo: string }>({
    mutationFn: ({ id, motivo }) => cliente.pedidosBc.rechazar(id, { motivo }),
    onSuccess: (respuesta) => {
      if (respuesta.data) {
        toast.success('Solicitud rechazada');
        qc.invalidateQueries({ queryKey: ['pedidos-bc-solicitudes'] });
        qc.invalidateQueries({ queryKey: ['colada', respuesta.data.id_colada] });
        qc.invalidateQueries({ queryKey: ['coladas'] });
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo rechazar la solicitud', { description: e.message });
    },
  });
}
